import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Breadcrumbs, BreadcrumbItem, EmptyState, LoadingState } from '../components/ui';
import { alert } from '../utils/toast';
import type { IntegrationRoute } from '../types';
import { Network, Plus, Edit2, Shuffle, Trash2 } from 'lucide-react';

export const IntegrationRoutes: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: routes, isLoading } = useQuery({
    queryKey: ['integration-routes'],
    queryFn: async () => {
      try {
        const response = await api.get<{ routes: IntegrationRoute[] }>('/admin/integration-routes');
        return response.data.routes || [];
      } catch {
        console.warn('Failed to fetch integration routes, using fallback');
        return [];
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/integration-routes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-routes'] });
      void alert.success('Eliminado!', 'Rota eliminada com sucesso!');
    },
  });

  const handleDelete = async (route: IntegrationRoute) => {
    const result = await alert.confirm(
      'Eliminar Rota?',
      `Tem a certeza de que deseja eliminar a rota "${route.name}"?`
    );

    if (result.isConfirmed) {
      deleteMutation.mutate(route.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Rotas de Integração</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={2} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Rotas de Integração</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <Network className="w-8 h-8 text-primary-500" />
            <span>Rotas de Integração</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Configure integrações com APIs externas
          </p>
        </div>
        <Link
          to="/integration-routes/new"
          className="btn-base btn-md btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Criar Rota</span>
        </Link>
      </div>

      {routes && routes.length > 0 ? (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Caminho do Endpoint</th>
                  <th>Método</th>
                  <th>URL Externa</th>
                  <th>Autenticação</th>
                  <th>Versão</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((route) => (
                  <tr key={route.id}>
                    <td className="font-semibold text-slate-900 dark:text-white">{route.name}</td>
                    <td className="font-mono text-xs text-slate-600 dark:text-slate-300">
                      {route.endpoint_path}
                    </td>
                    <td>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-800/50">
                        {route.http_method}
                      </span>
                    </td>
                    <td className="font-mono text-xs max-w-xs truncate text-slate-600 dark:text-slate-400">
                      {route.external_url}
                    </td>
                    <td className="capitalize text-slate-700 dark:text-slate-300">
                      {route.auth_method.replace('_', ' ')}
                    </td>
                    <td className="text-slate-600 dark:text-slate-400">v{route.version}</td>
                    <td>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/integration-routes/${route.id}`}
                          className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 text-xs font-semibold"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                          <span>Editar</span>
                        </Link>
                        <Link
                          to={`/integration-routes/${route.id}/mappings`}
                          className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 text-xs font-semibold"
                        >
                          <Shuffle className="w-3.5 h-3.5" />
                          <span>Mapeamentos</span>
                        </Link>
                        <button
                          onClick={() => handleDelete(route)}
                          className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 hover:text-rose-700 text-xs font-semibold"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs text-center py-12 px-6">
          <EmptyState
            title="Nenhuma rota de integração configurada"
            description="Crie rotas de integração para expor APIs externas através do gateway."
          />
          <Link
            to="/integration-routes/new"
            className="btn-base btn-md btn-primary mt-6 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            <span>Criar a Sua Primeira Rota</span>
          </Link>
        </div>
      )}
    </div>
  );
};
